import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ShopContext } from "../context/ShopContext";
import RelatedProducts from "../components/RelatedProducts";
import Reviews from "../components/Reviews";
import RatingStars from "../components/RatingStars";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const Product = () => {
  const { productId } = useParams();
  const { products, currency, addToCart, reviews, token, cartItems, setCartItems, backendUrl } =
    useContext(ShopContext);
  const navigate = useNavigate();
  const [productData, setProductData] = useState(null);
  const [currentImage, setCurrentImage] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [showMeasurementInput, setShowMeasurementInput] = useState(false);
  const [height, setHeight] = useState("");
  const [width, setWidth] = useState("");
  const [unit, setUnit] = useState("cm");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [redirectTimeoutId, setRedirectTimeoutId] = useState(null);

  const [pincode, setPincode] = useState("");
  const [locationAvailabilityMessage, setLocationAvailabilityMessage] =
    useState(null); 
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);
  const [showLocationPermissionModal, setShowLocationPermissionModal] =
    useState(false);
  const [showLocationExplanationModal, setShowLocationExplanationModal] =
    useState(false); 
  const [loading, setLoading] = useState(true);

  const getAverageRating = useCallback(
    (id) => {
      const productReviews = reviews.filter(
        (review) => review.productId === id
      );
      if (productReviews.length === 0) return 0;
      const sum = productReviews.reduce(
        (acc, review) => acc + review.rating,
        0
      );
      return sum / productReviews.length;
    },
    [reviews]
  );
  const avgRating = useMemo(
    () => (productData ? getAverageRating(productData._id) : 0),
    [productData, reviews]
  );
  const reviewCount = useMemo(
    () =>
      productData
        ? reviews.filter((r) => r.productId === productData._id).length
        : 0,
    [productData, reviews]
  );

  const fetchProductData = useCallback(() => {
    const foundProduct = products.find((item) => item._id === productId);
    if (foundProduct) {
      setProductData(foundProduct);
      setCurrentImage(foundProduct.image[0]);
      if (foundProduct.sizes && foundProduct.sizes.length > 0) {
        setSelectedSize(null);
      }
    }
  }, [productId, products]);

  useEffect(() => {
    setLoading(true);
    fetchProductData();
    setTimeout(() => setLoading(false), 800); 
  }, [fetchProductData]);

  useEffect(() => {
    let timer;
    if (showLoginModal && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      setRedirectTimeoutId(timer);
    } else if (showLoginModal && countdown === 0) {
      navigate("/login");
    }
    return () => clearTimeout(timer);
  }, [showLoginModal, countdown, navigate]);

  const handleAddToCartClick = useCallback(
    (id, size, color) => {
      if (!size || !color) return;
      if (!token) {
        setShowLoginModal(true);
        setCountdown(5);
      } else {
        // Prepare measurements object if present
        let measurements = null;
        if (showMeasurementInput && height && width && unit) {
          measurements = {
            H: height,
            W: width,
            measurementUnit: unit
          };
        }
        addToCart(id, size, color, measurements, true); // show toast
      }
    },
    [addToCart, token, showMeasurementInput, height, width, unit]
  );

  const handleBuyNowClick = useCallback(
    (id, size, color) => {
      if (!size || !color) return;
      if (!token) {
        setShowLoginModal(true);
        setCountdown(5);
      } else {
        // Prepare measurements object if present
        let measurements = null;
        if (showMeasurementInput && height && width && unit) {
          measurements = {
            H: height,
            W: width,
            measurementUnit: unit
          };
        }
        addToCart(id, size, color, measurements, false); // suppress toast
        navigate("/cart");
      }
    },
    [addToCart, navigate, token, showMeasurementInput, height, width, unit]
  );

  const handleCloseLoginModal = useCallback(() => {
    setShowLoginModal(false);
    setCountdown(5);
    if (redirectTimeoutId) {
      clearTimeout(redirectTimeoutId);
      setRedirectTimeoutId(null);
    }
  }, [redirectTimeoutId]);

  const handleLoginRedirectNow = useCallback(() => {
    handleCloseLoginModal();
    navigate("/login");
  }, [handleCloseLoginModal, navigate]);

  
  const getDeliveryEstimate = useCallback((code) => {
    const shopPincode = 676551;
    const enteredCodeNum = parseInt(code, 10);
    const shopCodeNum = parseInt(shopPincode, 10);

    const diff = Math.abs(enteredCodeNum - shopCodeNum);

    let distance = "";
    let days = "";

    if (diff === 0) {
      // Exact match to shop's pincode
      distance = "within 5 km";
      days = "1 day";
    } else if (diff < 500) {
      // Close by within Malappuram (simulated range)
      distance = "approx. 10-25 km";
      days = "1-2 days";
    } else if (diff < 2000) {
      // Further within Malappuram (simulated range)
      distance = "approx. 25-50 km";
      days = "2-3 days";
    } else {
      // At the edges of Malappuram or slightly outside with a 676xxx prefix (simulated)
      distance = "approx. 50-80 km";
      days = "3-4 days";
    }
    return { distance, days };
  }, []);

  // Function to check if a pincode is in Malappuram, Kerala
  const checkPincodeAvailability = useCallback(
    (code) => {
      // Malappuram pincodes generally start with 676
      const isMalappuram = code.startsWith("676") && code.length === 6;
      if (isMalappuram) {
        const estimate = getDeliveryEstimate(code);
        setLocationAvailabilityMessage({
          type: "success",
          text: "Great news! Installation service available in your area.",
          distance: estimate.distance,
          days: estimate.days,
        });
      } else {
        setLocationAvailabilityMessage({
          type: "error",
          text: "Service not available in this area. We currently serve only Malappuram district.",
        });
      }
      setIsCheckingLocation(false);
    },
    [getDeliveryEstimate]
  );

  const handlePincodeCheck = useCallback(() => {
    if (pincode.length === 6 && /^\d+$/.test(pincode)) {
      setIsCheckingLocation(true);
      setLocationAvailabilityMessage(null);
      setTimeout(() => checkPincodeAvailability(pincode), 1000);
    } else {
      setLocationAvailabilityMessage({
        type: "error",
        text: "Please enter a valid 6-digit pincode.",
      });
    }
  }, [pincode, checkPincodeAvailability]);

  const handleUseMyLocationSimulated = useCallback(() => {
    setIsCheckingLocation(true);
    setLocationAvailabilityMessage(null);
    setShowLocationPermissionModal(false);

    // Simulate fetching current location and getting a Malappuram pincode
    setTimeout(() => {
      const simulatedMalappuramPincode = "676505"; // Example Malappuram pincode
      setPincode(simulatedMalappuramPincode);
      checkPincodeAvailability(simulatedMalappuramPincode);
    }, 1500);
  }, [checkPincodeAvailability]);

  const handleUseMyLocationAccurate = useCallback(() => {
    setIsCheckingLocation(true);
    setLocationAvailabilityMessage(null);
    setShowLocationPermissionModal(false);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          const malappuramPincodeRange = {
            minLat: 10.7,
            maxLat: 11.4, // Approximate latitude range for Malappuram
            minLon: 75.8,
            maxLon: 76.8, // Approximate longitude range for Malappuram
          };

          // Simulate a pincode based on a Malappuram coordinate.
          // In a real scenario, you'd use a reverse geocoding API to get pincode from lat/lon.
          if (
            lat >= malappuramPincodeRange.minLat &&
            lat <= malappuramPincodeRange.maxLat &&
            lon >= malappuramPincodeRange.minLon &&
            lon <= malappuramPincodeRange.maxLon
          ) {
            const simulatedMalappuramPincode = "676505"; // Default Malappuram pincode
            setPincode(simulatedMalappuramPincode);
            checkPincodeAvailability(simulatedMalappuramPincode);
          } else {
            setLocationAvailabilityMessage({
              type: "error",
              text: "Your current location is outside our service area (Malappuram, Kerala).",
            });
            setIsCheckingLocation(false);
          }
        },
        (error) => {
          setIsCheckingLocation(false);
          setShowLocationPermissionModal(true); // Show modal if permission denied or error
          console.error("Geolocation error:", error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setIsCheckingLocation(false);
      setLocationAvailabilityMessage({
        type: "error",
        text: "Geolocation is not supported by your browser.",
      });
    }
  }, [checkPincodeAvailability]);

  const discount = useMemo(
    () =>
      productData && productData.originalPrice > productData.price
        ? Math.round(
            ((productData.originalPrice - productData.price) /
              productData.originalPrice) *
              100
          )
        : 0,
    [productData]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 animate-pulse">
        <div className="w-full max-w-4xl mx-auto p-8 rounded-2xl bg-white/80 shadow-lg flex flex-col gap-8">
          <div className="h-72 bg-gray-200 rounded-xl mb-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-gradient-x" style={{backgroundSize:'200% 100%'}}></div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="h-8 w-2/3 bg-gray-200 rounded"></div>
            <div className="h-6 w-1/3 bg-gray-200 rounded"></div>
            <div className="h-10 w-1/2 bg-gray-200 rounded"></div>
            <div className="h-4 w-1/4 bg-gray-200 rounded"></div>
          </div>
          <div className="flex gap-4 mt-8">
            <div className="h-12 w-32 bg-gray-200 rounded-full"></div>
            <div className="h-12 w-32 bg-gray-200 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t-2 pt-10 px-4 md:px-8 lg:px-16 max-w-7xl mx-auto pb-10 font-inter text-gray-800">
      {/* Main Product Container */}
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
        {/* Image Gallery */}
        <div className="flex flex-col sm:flex-row-reverse lg:flex-row-reverse gap-4 flex-1 lg:max-w-[45%] lg:sticky lg:top-8 lg:self-start">
          {/* Main Image */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full sm:w-[80%] lg:w-full relative aspect-square bg-gray-50 rounded-xl overflow-hidden flex items-center justify-center shadow-lg"
          >
            <img
              className="w-full h-full object-contain p-4"
              src={currentImage}
              alt={productData.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://placehold.co/600x600/cccccc/333333?text=Image+Error`;
              }}
            />
            {discount > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute top-3 right-3 bg-red-600 text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-md"
              >
                {discount}% OFF
              </motion.div>
            )}
          </motion.div>

          {/* Thumbnails */}
          <div className="flex flex-row sm:flex-col lg:flex-col overflow-x-auto sm:overflow-y-auto justify-start items-center sm:items-stretch w-full sm:w-[20%] lg:w-full lg:max-w-[120px] gap-2 p-1 no-scrollbar">
            {productData.image.map((item, index) => (
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                key={index}
                className="flex-shrink-0 w-24 h-24 sm:w-full sm:h-auto lg:w-24 lg:h-24 aspect-square snap-start"
              >
                <img
                  onClick={() => setCurrentImage(item)}
                  src={item}
                  className={`w-full h-full cursor-pointer border-2 transition-all duration-200 object-cover rounded-md ${
                    currentImage === item
                      ? "border-orange-500 scale-95 shadow-lg"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  alt={`Product thumbnail ${index + 1}`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://placehold.co/100x100/cccccc/333333?text=Thumb`;
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Product Info */}
        <div className="flex-1 lg:max-w-[55%]">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="font-semibold text-3xl md:text-4xl mt-2 text-gray-900">
              {productData.name}
            </h1>
            <div className="flex items-center mt-2 mb-4">
              <RatingStars rating={avgRating} />
              <span className="text-sm text-gray-700 ml-2">
                ({reviewCount} reviews)
              </span>
            </div>
            <p className="mt-5 text-3xl md:text-4xl font-extrabold flex items-center gap-3 text-gray-900">
              {currency}
              {productData.price}
              {productData.originalPrice > productData.price && (
                <span className="text-lg text-gray-500 line-through font-normal">
                  {currency}
                  {productData.originalPrice}
                </span>
              )}
            </p>
            {discount > 0 && (
              <p className="text-green-600 font-semibold text-lg mt-2 animate-pulse">
                Grab it now! Limited time offer.
              </p>
            )
            }
            <p className="mt-6 text-gray-800 leading-relaxed text-base">
              {productData.shortDescription ||
                "No short description available."}
            </p>
          </motion.div>

          {/* Size Selection */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col gap-4 my-8"
          >
            <div className="flex justify-between items-center">
              <p className="font-medium text-lg text-gray-900">Select Size</p>
              {selectedSize && (
                <button
                  onClick={() => {
                    setSelectedSize(null);
                    setShowMeasurementInput(false);
                    setHeight("");
                    setWidth("");
                  }}
                  className="text-sm text-gray-700 hover:text-gray-900 hover:underline transition-colors"
                >
                  Clear selection
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {productData.sizes?.length > 0 ? (
                productData.sizes.map((item, index) => (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSelectedSize(item);
                      setShowMeasurementInput(true);
                    }}
                    className={`border py-2 px-5 rounded-full text-base transition-all duration-200 min-w-[60px] text-center ${
                      item === selectedSize
                        ? "border-orange-500 bg-orange-50 text-orange-800 font-semibold shadow-inner"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                    }`}
                    key={index}
                  >
                    {item}
                  </motion.button>
                ))
              ) : (
                <span className="text-gray-700 text-base">
                  No sizes available for this product.
                </span>
              )}
            </div>
          </motion.div>

          {/* Color Selection */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="flex flex-col gap-4 my-8"
          >
            <div className="flex justify-between items-center">
              <p className="font-medium text-lg text-gray-900">Select Color</p>
              {selectedColor && (
                <button
                  onClick={() => setSelectedColor(null)}
                  className="text-sm text-gray-700 hover:text-gray-900 hover:underline transition-colors"
                >
                  Clear selection
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {productData.colors?.length > 0 ? (
                productData.colors.map((color, index) => (
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => setSelectedColor(color)}
                    key={index}
                    className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                      selectedColor === color
                        ? "border-orange-500 ring-2 ring-orange-300 scale-105"
                        : "border-gray-300 hover:border-orange-400"
                    }`}
                    style={{ backgroundColor: color.toLowerCase() }}
                    aria-label={`Select color ${color}`}
                  >
                    {(color.toLowerCase() === "white" ||
                      color.toLowerCase() === "#ffffff") && (
                      <span className="text-gray-400 text-xs font-semibold" style={{ transform: "scale(0.8)" }}>
                        W
                      </span>
                    )}
                    {selectedColor === color && (
                      <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </motion.button>
                ))
              ) : (
                <span className="text-gray-700 text-base">
                  No colors available for this product.
                </span>
              )}
            </div>
          </motion.div>

          {/* Measurement Box */}
          <div className="min-h-[160px]">
            <AnimatePresence>
              {showMeasurementInput && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border border-gray-200 p-5 mt-4 rounded-xl bg-white shadow-sm overflow-hidden"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold text-lg text-gray-900">
                      {selectedSize === "Custom"
                        ? "Custom Measurements (Optional)"
                        : "Optional Measurements"}
                    </h4>
                    <button
                      onClick={() => {
                        setShowMeasurementInput(false);
                        setHeight("");
                        setWidth("");
                      }}
                      className="text-gray-700 hover:text-gray-900 text-xl font-bold transition-colors duration-200"
                      aria-label="Close measurement input"
                    >
                      &times;
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label
                        htmlFor="height-input"
                        className="text-sm text-gray-700 mb-1"
                      >
                        Height:
                      </label>
                      <input
                        id="height-input"
                        type="number"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                        className="border border-gray-300 p-2.5 rounded-md focus:ring-orange-500 focus:border-orange-500 transition-all text-gray-900"
                        placeholder="e.g., 180"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label
                        htmlFor="width-input"
                        className="text-sm text-gray-700 mb-1"
                      >
                        Width:
                      </label>
                      <input
                        id="width-input"
                        type="number"
                        value={width}
                        onChange={(e) => setWidth(e.target.value)}
                        className="border border-gray-300 p-2.5 rounded-md focus:ring-orange-500 focus:border-orange-500 transition-all text-gray-900"
                        placeholder="e.g., 90"
                      />
                    </div>
                    <div className="flex flex-col sm:col-span-2">
                      <label
                        htmlFor="unit-select"
                        className="text-sm text-gray-700 mb-1"
                      >
                        Unit:
                      </label>
                      <select
                        id="unit-select"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className="border border-gray-300 p-2.5 rounded-md focus:ring-orange-500 focus:border-orange-500 transition-all bg-white text-gray-900"
                      >
                        <option value="cm">Centimeters (cm)</option>
                        <option value="m">Meters (m)</option>
                        <option value="mm">Millimeters (mm)</option>
                        <option value="inch">Inches (inch)</option>
                        <option value="ft">Feet (ft)</option>
                        <option value="yd">Yards (yd)</option>
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Action Buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 space-y-4"
          >
            <button
              onClick={() =>
                handleAddToCartClick(productData._id, selectedSize, selectedColor)
              }
              disabled={!selectedSize || !selectedColor}
              className={`px-8 py-3.5 text-lg rounded-full w-full transition-all duration-300 flex items-center justify-center gap-2 font-medium ${
                selectedSize && selectedColor
                  ? "bg-black text-white hover:bg-gray-800 hover:shadow-lg transform hover:-translate-y-0.5"
                  : "bg-gray-200 text-gray-700 cursor-not-allowed opacity-70"
              }`}
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                ></path>
              </svg>
              <span>ADD TO CART</span>
              {selectedSize && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="text-xs bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold"
                >
                  +
                </motion.span>
              )}
            </button>
            <button
              onClick={() => handleBuyNowClick(productData._id, selectedSize, selectedColor)}
              disabled={!selectedSize || !selectedColor}
              className={`px-8 py-3.5 text-lg rounded-full w-full transition-all duration-300 font-medium ${
                selectedSize && selectedColor
                  ? "bg-gray-900 text-white hover:bg-gray-800 hover:shadow-lg transform hover:-translate-y-0.5"
                  : "bg-gray-200 text-gray-700 cursor-not-allowed opacity-70"
              }`}
            >
              BUY NOW
            </button>
          </motion.div>

          <hr className="my-10 border-gray-200" />
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-base text-gray-800 space-y-3"
          >
            <p>
              <span className="font-semibold text-gray-900">Category:</span>{" "}
              {productData.category}
            </p>
            <p>
              <span className="font-semibold text-gray-900">Sub-category:</span>{" "}
              {productData.subCategory}
            </p>
            {/* 100% Original Product */}
            <div className="flex items-center gap-2 text-green-700 font-medium">
              <svg
                className="w-5 h-5 text-green-700"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Authentic Product Guaranteed</span>
            </div>
            {/* Cash on delivery available */}
            <div className="flex items-center gap-2 text-blue-700 font-medium">
              <svg
                className="w-5 h-5 text-blue-700"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6zm1 6h6v2H7v-2z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Cash on Delivery Available</span>
            </div>
            {/* Call to action for support */}
            <p className="text-gray-900 pt-3 font-medium">
              Need assistance?{" "}
              <Link to="/contact" className="text-orange-500 hover:underline">
                Contact our support team
              </Link>{" "}
              for any queries.
            </p>
            {/* Link to Privacy Policies Page */}
            <p className="text-gray-900 font-medium mt-4">
              For details on our refund, return, and exchange policies, please
              visit our{" "}
              <Link
                to="/privacy-policies"
                className="text-blue-600 hover:underline"
              >
                Privacy Policies page
              </Link>
              .
            </p>
          </motion.div>
        </div>
      </div>

      {/* Location Availability Check */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-20 p-6 bg-white rounded-xl shadow-lg border border-gray-100"
      >
        <h2 className="font-bold text-2xl mb-4 text-gray-900 flex items-center gap-2">
          <svg
            className="w-6 h-6 text-gray-800"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
              clipRule="evenodd"
            ></path>
          </svg>
          Check Installation Availability
          <button
            onClick={() => setShowLocationExplanationModal(true)}
            className="ml-2 w-6 h-6 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-bold hover:bg-gray-200 transition-colors"
            aria-label="What are these location options?"
          >
            ?
          </button>
        </h2>
        <p className="text-gray-800 mb-4">
          We currently offer professional installation services only within
          Malappuram district, Kerala.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="6"
            value={pincode}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value) || value === "") {
                setPincode(value);
              }
            }}
            placeholder="Enter 6-digit Pincode"
            className="flex-grow p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900"
            aria-label="Pincode input"
            disabled={isCheckingLocation}
          />
          <button
            onClick={handlePincodeCheck}
            className={`px-6 py-3 rounded-md text-white font-medium transition-all duration-300 flex items-center gap-2 ${
              isCheckingLocation
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gray-600 hover:bg-gray-700 shadow-md transform hover:-translate-y-0.5"
            }`}
            disabled={isCheckingLocation}
          >
            {isCheckingLocation ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Checking...
              </>
            ) : (
              "Check Pincode"
            )}
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleUseMyLocationSimulated}
            className={`w-full sm:w-1/2 px-6 py-3 rounded-md text-gray-800 border border-gray-400 font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
              isCheckingLocation
                ? "bg-gray-100 cursor-not-allowed opacity-70"
                : "hover:bg-gray-50 hover:shadow-sm transform hover:-translate-y-0.5"
            }`}
            disabled={isCheckingLocation}
          >
            <svg
              className="w-5 h-5 text-gray-700"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              ></path>
            </svg>
            {isCheckingLocation ? "Locating..." : "Use My Location (Simulated)"}
          </button>
          <button
            onClick={handleUseMyLocationAccurate}
            className={`w-full sm:w-1/2 px-6 py-3 rounded-md text-gray-800 border border-gray-400 font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
              isCheckingLocation
                ? "bg-gray-100 cursor-not-allowed opacity-70"
                : "hover:bg-gray-50 hover:shadow-sm transform hover:-translate-y-0.5"
            }`}
            disabled={isCheckingLocation}
          >
            <svg
              className="w-5 h-5 text-gray-700"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              ></path>
            </svg>
            {isCheckingLocation ? "Locating..." : "Use My Location (Accurate)"}
          </button>
        </div>

        <AnimatePresence mode="wait">
          {locationAvailabilityMessage && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className={`p-3 rounded-md text-center mt-3 ${
                locationAvailabilityMessage.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              } font-medium`}
            >
              {locationAvailabilityMessage.type === "success" ? (
                <>
                  <p className="flex items-center justify-center gap-2 text-gray-800">
                    <svg
                      className="w-5 h-5 text-green-700"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span className="font-semibold">
                      {locationAvailabilityMessage.text}
                    </span>
                  </p>
                  {locationAvailabilityMessage.distance && (
                    <p className="mt-2 text-gray-800 flex items-center justify-center gap-2">
                      <svg
                        className="w-5 h-5 text-gray-700"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      Distance From the Shop:{" "}
                      <span className="font-semibold">
                        {locationAvailabilityMessage.distance}
                      </span>
                    </p>
                  )}
                  {locationAvailabilityMessage.days && (
                    <p className="mt-1 text-gray-800 flex items-center justify-center gap-2">
                      <svg
                        className="w-5 h-5 text-gray-700"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 9.586V6z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      Approx. Delivery Time:{" "}
                      <span className="font-semibold">
                        {locationAvailabilityMessage.days}
                      </span>
                    </p>
                  )}
                  <p className="text-sm text-gray-700 mt-2">
                    If you buy this product today
                  </p>
                </>
              ) : (
                <p className="flex items-center justify-center gap-2 text-gray-800">
                  <svg
                    className="w-5 h-5 text-red-700"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  <span className="font-semibold">
                    {locationAvailabilityMessage.text}
                  </span>
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <p className="text-sm text-gray-700 mt-2">
          Our shop is located at Kadungathukundu, Kalpakanchery, Malappuram,
          Kerala (Pincode: 676551).
        </p>
      </motion.div>

      {/* Product Tabs */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-20"
      >
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setShowMoreInfo(false)}
            className={`px-6 py-4 text-lg transition-all duration-200 relative ${
              !showMoreInfo
                ? "font-bold text-gray-900 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gray-900"
                : "text-gray-700 hover:text-gray-900"
            }`}
          >
            Description
          </button>
          <button
            onClick={() => setShowMoreInfo(true)}
            className={`px-6 py-4 text-lg transition-all duration-200 relative ${
              showMoreInfo
                ? "font-bold text-gray-900 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gray-900"
                : "text-gray-700 hover:text-gray-900"
            }`}
          >
            Specifications
          </button>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={showMoreInfo ? "specs" : "desc"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="border border-t-0 border-gray-200 p-6 text-gray-800 leading-relaxed rounded-b-xl shadow-sm"
          >
            {!showMoreInfo ? (
              <p className="text-gray-800">
                {productData.description ||
                  "No detailed description available."}
              </p>
            ) : (
              <>
                <h3 className="font-bold text-xl mb-4 text-gray-900">
                  Product Specifications
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Category and Sub-category (Always shown as they are generally required) */}
                      <tr>
                        <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                          Category
                        </td>
                        <td className="py-3 px-4 text-gray-900">
                          {productData.category}
                        </td>
                      </tr>
                      {productData.subCategory && ( // Show only if subCategory exists
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Sub-category
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.subCategory}
                          </td>
                        </tr>
                      )}

                      {/* Basic Product Attributes - show if array has length > 0 */}
                      {productData.colors?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Colors
                          </td>
                          <td className="py-3 px-4 flex flex-wrap gap-2 items-center">
                            {productData.colors.map((color, index) => (
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                key={index}
                                className="w-6 h-6 rounded-full border border-gray-300 shadow-sm flex items-center justify-center"
                                style={{ backgroundColor: color.toLowerCase() }}
                                title={color}
                              >
                                {(color.toLowerCase() === "white" ||
                                  color.toLowerCase() === "#ffffff") && (
                                  <span
                                    className="text-gray-400 text-xs font-semibold"
                                    style={{ transform: "scale(0.8)" }}
                                  >
                                    W
                                  </span>
                                )}
                              </motion.div>
                            ))}
                          </td>
                        </tr>
                      )}
                      {productData.sizes?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Available Sizes
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.sizes.join(", ")}
                          </td>
                        </tr>
                      )}
                      {productData.materials?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Materials
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.materials.join(", ")}
                          </td>
                        </tr>
                      )}

                      {/* General Product Information - show if string is not empty/null */}
                      {productData.brand && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Brand
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.brand}
                          </td>
                        </tr>
                      )}
                      {productData.modelNumber && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Model Number
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.modelNumber}
                          </td>
                        </tr>
                      )}
                      {productData.itemWeight && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Item Weight
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.itemWeight}
                          </td>
                        </tr>
                      )}
                      {productData.countryOfOrigin && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Country of Origin
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.countryOfOrigin}
                          </td>
                        </tr>
                      )}
                      {productData.manufacturer && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Manufacturer
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.manufacturer}
                          </td>
                        </tr>
                      )}
                      {productData.manufacturedDate && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Manufactured Date
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {new Date(
                              productData.manufacturedDate
                            ).toLocaleDateString()}
                          </td>
                        </tr>
                      )}
                      {productData.guarantee && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Guarantee
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.guarantee}
                          </td>
                        </tr>
                      )}

                      {/* Curtain-Specific Attributes - show if array has length > 0 */}
                      {productData.pattern?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Pattern
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.pattern.join(", ")}
                          </td>
                        </tr>
                      )}
                      {productData.headingStyle?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Heading Style
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.headingStyle.join(", ")}
                          </td>
                        </tr>
                      )}
                      {productData.liningType?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Lining Type
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.liningType.join(", ")}
                          </td>
                        </tr>
                      )}
                      {productData.roomType?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Recommended Room
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.roomType.join(", ")}
                          </td>
                        </tr>
                      )}
                      {productData.opacity?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Opacity
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.opacity.join(", ")}
                          </td>
                        </tr>
                      )}
                      {productData.careInstructions?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Care Instructions
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.careInstructions.join(", ")}
                          </td>
                        </tr>
                      )}
                      {productData.feature?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Features
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.feature.join(", ")}
                          </td>
                        </tr>
                      )}
                      {productData.style?.length > 0 && (
                        <tr>
                          <td className="py-3 px-4 font-medium bg-gray-50 w-1/3 text-gray-800">
                            Style
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            {productData.style.join(", ")}
                          </td>
                        </tr>
                      )}

                      {/* Fallback for no additional specifications */}
                      {/* This checks if ANY of the optional fields have data. If all are empty, then show the "No additional specs" message. */}
                      {!productData.colors?.length &&
                        !productData.sizes?.length &&
                        !productData.materials?.length &&
                        !productData.brand &&
                        !productData.modelNumber &&
                        !productData.itemWeight &&
                        !productData.countryOfOrigin &&
                        !productData.manufacturer &&
                        !productData.manufacturedDate &&
                        !productData.guarantee &&
                        !productData.pattern?.length &&
                        !productData.headingStyle?.length &&
                        !productData.liningType?.length &&
                        !productData.roomType?.length &&
                        !productData.opacity?.length &&
                        !productData.careInstructions?.length &&
                        !productData.feature?.length &&
                        !productData.style?.length && (
                          <tr>
                            <td
                              colSpan="2"
                              className="py-4 text-center text-gray-700"
                            >
                              No additional specifications available for this
                              product.
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Reviews Section */}
      <div className="mt-20">
        <Reviews productId={productId} />
      </div>

      {/* Related Products */}
      <RelatedProducts
        category={productData.category}
        subCategory={productData.subCategory}
      />

      {/* Login Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm text-center transform scale-100"
            >
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                    ></path>
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                Login Required
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                You need to log in first to add items to your cart or make
                purchases.
              </p>
              <p className="text-md text-gray-700 mb-7 font-semibold">
                Redirecting to login in {countdown} seconds...
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCloseLoginModal}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-100 transition-all duration-200 font-medium"
                >
                  Stay Here
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLoginRedirectNow}
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-200 font-medium"
                >
                  Login Now
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Location Permission Modal */}
      <AnimatePresence>
        {showLocationPermissionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center transform scale-100"
            >
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.727A8 8 0 016.343 5.273L17.657 16.727zm0 0L19.5 18.5m-1.843-1.773l1.843 1.773M12 22a10 10 0 100-20 10 10 0 000 20zM9.5 12.5a2 2 0 11-4 0 2 2 0 014 0zm5 0a2 2 0 11-4 0 2 2 0 014 0z"
                    ></path>
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                Location Access Required
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                To accurately check service availability by your location,
                please enable location services for your browser or device and
                grant permission to this website.
              </p>
              <ul className="text-left text-gray-700 mb-6 space-y-2 text-sm list-disc list-inside">
                <li>
                  Check your browser's address bar for a location icon or
                  prompt.
                </li>
                <li>
                  Go to your device's settings (Privacy & Security) and ensure
                  location access is allowed for your browser.
                </li>
                <li>
                  Ensure you are not using a VPN or proxy that might mask your
                  real location.
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowLocationPermissionModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-100 transition-all duration-200 font-medium"
                >
                  Close
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleUseMyLocationAccurate} // Retry getting location
                  className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all duration-200 font-medium"
                >
                  Try Again
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowLocationPermissionModal(false);
                    navigate("/troubleshooting/location-accurate-checking");
                  }}
                  className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 font-medium"
                >
                  Troubleshoot
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Location Explanation Modal */}
      <AnimatePresence>
        {showLocationExplanationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center transform scale-100"
            >
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setShowLocationExplanationModal(false)}
                  className="text-gray-600 hover:text-gray-900 text-3xl font-bold transition-colors duration-200"
                  aria-label="Close explanation"
                >
                  &times;
                </button>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                Understanding Location Options
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                We offer two ways to check service availability for your
                convenience:
              </p>
              <div className="space-y-6 text-left">
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-gray-800"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    Use My Location (Simulated)
                  </h4>
                  <p className="text-gray-800 text-sm mt-1">
                    This option provides a quick check based on a **simulated
                    Malappuram pincode (676505)**. It does not access your
                    actual device location and is useful for a general idea of
                    service availability.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-gray-800"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    Use My Location (Accurate)
                  </h4>
                  <p className="text-gray-800 text-sm mt-1">
                    This option uses your device's **GPS and network
                    information** for a more precise location check. It requires
                    your permission to access your location. This helps us
                    provide a more accurate estimate of distance and delivery
                    time.
                  </p>
                </div>
              </div>
              <Link
                to="/troubleshooting/location-accurate-checking"
                className="inline-block mt-8 text-blue-600 hover:underline text-base font-medium"
              >
                Having trouble with accurate location? Visit our troubleshooting
                guide.
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Product;
